import { useCallback } from "react";
import { notification } from "antd";
import { useTranslate } from "@refinedev/core";
import { Slide } from "pages/media/types/";
import { useTemplateData } from "./useTemplateData";
import { useSaveSlidesAsset } from "./useSaveSlidesAsset";
import { useSaveStoryboard } from "../../storyboard/hooks/useSaveStoryboard";
import { reorderScriptSlides } from "pages/media/projects/components/slides/utils";

const END = Number.MAX_SAFE_INTEGER;

/**
 * Persists drag-and-drop reordering of scenes (within a chapter) and of
 * chapters themselves. Order is array-position only across all assets, so each
 * reorder updates the relevant cache optimistically and writes it back:
 *  - scenes  -> slides asset (the order source) + the narration script
 *  - chapters -> storyboard asset sections (+ media-generation chapter rows)
 *
 * The chapter template's slide definitions are keyed by name (slide uuid) and
 * their array order has no effect on generation, so scene reordering does NOT
 * touch the template.
 */
export const useSceneReorder = () => {
  const t = useTranslate();
  const [api, contextHolder] = notification.useNotification();

  const {
    projectId,
    organizationId,
    templatesData: { storyboard: storyboardMeta, storyboardId, primaryDocumentId },
    storyboardSections,
    mediaGenerationSettingsData: { reorderChapters },
    storyboardWithSlidesData: {
      slides,
      setSlides,
      reorderSections,
      updateSection,
      storyboard: storyboardData,
      refetchSlides,
      refetchStoryboard,
    },
  } = useTemplateData();

  const { saveSlidesAsset } = useSaveSlidesAsset({
    organizationId: organizationId ?? undefined,
    projectId: projectId ?? undefined,
  });

  const { saveStoryboard } = useSaveStoryboard({
    projectId: projectId ?? "",
    organizationId: organizationId ?? "",
    storyboardId: storyboardId ?? undefined,
    documentId: primaryDocumentId ?? undefined,
  });

  const slidesAssetId = storyboardMeta?.slides_asset_id;

  /** Reorder the (non-transition) scenes of one chapter. */
  const reorderScenes = useCallback(
    async (sectionId: string, orderedSceneUuids: string[]) => {
      if (!slidesAssetId) return;

      const sceneSet = new Set(orderedSceneUuids);
      const orderedObjs = orderedSceneUuids
        .map((uuid) => slides.find((s) => s.uuid === uuid))
        .filter((s): s is Slide => !!s);
      if (orderedObjs.length !== orderedSceneUuids.length) return;

      // Replace this chapter's scenes in their existing slots with the new
      // order; slides of other chapters (and transitions) stay put.
      let cursor = 0;
      const nextSlides = slides.map((slide) =>
        sceneSet.has(slide.uuid) ? orderedObjs[cursor++] : slide
      );

      const prevSlides = slides;
      setSlides(nextSlides); // optimistic

      // The section's narration script anchors each slide's text by uuid, so it
      // must follow the new order of the section's slides (scenes + transition).
      const sectionStoryboard = storyboardData?.sections.find(
        (s) => s.id === sectionId
      );
      const currentScript = sectionStoryboard?.script?.content ?? "";
      const nextSectionUuids = nextSlides
        .filter((s) => s.section_id === sectionId)
        .map((s) => s.uuid);
      const nextScript = reorderScriptSlides(currentScript, nextSectionUuids);
      const scriptChanged =
        !!sectionStoryboard && nextScript !== currentScript;
      const nextSectionScript = sectionStoryboard
        ? { ...sectionStoryboard.script, content: nextScript }
        : undefined;

      if (scriptChanged) {
        updateSection(sectionId, { script: nextSectionScript }); // optimistic
      }

      try {
        // Write the slides asset first (the order source) so a failure here
        // strands nothing else, then the narration script. The Slides PATCH
        // is what marks the media out of date — BE does it for that asset
        // type, so there's no explicit mark here. The chapter
        // template is intentionally left untouched: its slide definitions are
        // keyed by name and order-independent, so reordering must not rewrite
        // it (doing so also risked dropping slides added this session, since
        // the editor's serverTemplates snapshot isn't updated on add).
        await saveSlidesAsset(slidesAssetId, nextSlides);

        if (scriptChanged && storyboardData) {
          await saveStoryboard({
            ...storyboardData,
            sections: storyboardData.sections.map((s) =>
              s.id === sectionId ? { ...s, script: nextSectionScript } : s
            ),
          });
        }
      } catch (error) {
        console.error("Error reordering scenes", error);
        // Revert the optimistic cache, then refetch the affected assets to
        // reconcile with whatever actually persisted on the server.
        setSlides(prevSlides);
        if (scriptChanged && sectionStoryboard) {
          updateSection(sectionId, { script: sectionStoryboard.script });
        }
        refetchSlides().catch(() => undefined);
        refetchStoryboard().catch(() => undefined);
        api.error({
          message: t(
            "components.templates.TemplatesBoard.storyboardLeftPanel.reorderError"
          ),
        });
      }
    },
    [
      slidesAssetId,
      slides,
      setSlides,
      updateSection,
      storyboardData,
      saveStoryboard,
      saveSlidesAsset,
      refetchSlides,
      refetchStoryboard,
      api,
      t,
    ]
  );

  /** Reorder chapters to match the given section-id order. */
  const reorderChaptersOrder = useCallback(
    async (orderedSectionIds: string[]) => {
      const prevOrder = storyboardSections.map((s) => s.id);
      reorderSections(orderedSectionIds); // optimistic

      try {
        // Settings first: it now throws on failure, so a failure here stops
        // before the storyboard write and reverts cleanly. It also marks the
        // media for us, so this path adds no mark of its own.
        await reorderChapters(orderedSectionIds);
        if (storyboardData) {
          const rank = new Map(
            orderedSectionIds.map((id, i) => [id, i])
          );
          const sections = [...storyboardData.sections].sort(
            (a, b) => (rank.get(a.id) ?? END) - (rank.get(b.id) ?? END)
          );
          await saveStoryboard({ ...storyboardData, sections });
        }
      } catch (error) {
        console.error("Error reordering chapters", error);
        reorderSections(prevOrder); // revert storyboard cache
        // Best-effort: restore the previous chapter order in the settings and
        // refetch the storyboard so the cache matches the server.
        reorderChapters(prevOrder).catch(() => undefined);
        refetchStoryboard().catch(() => undefined);
        api.error({
          message: t(
            "components.templates.TemplatesBoard.storyboardLeftPanel.reorderError"
          ),
        });
      }
    },
    [
      storyboardSections,
      reorderSections,
      reorderChapters,
      storyboardData,
      saveStoryboard,
      refetchStoryboard,
      api,
      t,
    ]
  );

  return { reorderScenes, reorderChapters: reorderChaptersOrder, contextHolder };
};
